import React, { useState, useEffect } from 'react';
import { useToast } from '../contexts/ToastContext';
import { investmentSheetCrvService, investmentSheetPhService, investmentSheetCraService } from '../services/investmentSheetService';
import { InvestmentSheetCrv, InvestmentSheetPh, InvestmentSheetCra, SheetType, SHEET_TYPE_LABELS } from '../types/InvestmentSheet';
import { investorService } from '../services/userService';
import { crvProductService, craProductService, phProductService } from '../services/productService';
import { Investor } from '../types/User';
import InvestmentCrvForm from '../components/investments/InvestmentCrvForm';
import InvestmentPhForm from '../components/investments/InvestmentPhForm';
import InvestmentCraForm from '../components/investments/InvestmentCraForm';
import AssignTicketsModal from '../components/investments/AssignTicketsModal';
import ConfirmModal from '../components/common/ConfirmModal';
import ActionButton from '../components/common/ActionButton';
import LoadingSpinner from '../components/common/LoadingSpinner';
import EmptyState from '../components/common/EmptyState';
import { FaPlus, FaEdit, FaTrash, FaTicketAlt } from 'react-icons/fa';
import { BsGraphUp } from 'react-icons/bs';
import './Investment.css';

const Investment: React.FC = () => {
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState<SheetType>('crv');
  const [crvSheets, setCrvSheets] = useState<InvestmentSheetCrv[]>([]);
  const [phSheets, setPhSheets] = useState<InvestmentSheetPh[]>([]);
  const [craSheets, setCraSheets] = useState<InvestmentSheetCra[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingSheet, setEditingSheet] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [sheetToDelete, setSheetToDelete] = useState<{ id: string; name: string } | null>(null);
  const [showAssignTicketsModal, setShowAssignTicketsModal] = useState(false);
  const [assignTicketsSheet, setAssignTicketsSheet] = useState<{ id: string; investorName: string } | null>(null);

  // Datos para mostrar nombres
  const [investors, setInvestors] = useState<Investor[]>([]);
  const [products, setProducts] = useState<any[]>([]);

  // Filtros
  const [filterInvestor, setFilterInvestor] = useState('');
  const [filterProduct, setFilterProduct] = useState('');
  const [filterImporte, setFilterImporte] = useState('');
  const [filterEstado, setFilterEstado] = useState('');

  // Paginación
  const PAGE_SIZE = 25;
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    loadInvestors();
  }, []);

  useEffect(() => {
    setShowForm(false);
    setEditingSheet(null);
    setCurrentPage(1);
    loadData();
    loadProducts();
  }, [activeTab]);

  const loadInvestors = async () => {
    try {
      const response = await investorService.getAll();
      setInvestors(response.data);
    } catch (error) {
      console.error('Error loading investors:', error);
    }
  };

  const loadProducts = async () => {
    try {
      if (activeTab === 'crv') {
        const productsRes = await crvProductService.getAll();
        setProducts(productsRes.data);
      } else if (activeTab === 'cra') {
        const productsRes = await craProductService.getAll();
        setProducts(productsRes.data);
      } else {
        const productsRes = await phProductService.getAll();
        setProducts(productsRes.data);
      }
    } catch (error) {
      console.error('Error loading products:', error);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'crv') {
        const response = await investmentSheetCrvService.getAll();
        setCrvSheets(response.data);
      } else if (activeTab === 'cra') {
        const response = await investmentSheetCraService.getAll();
        setCraSheets(response.data);
      } else {
        const response = await investmentSheetPhService.getAll();
        setPhSheets(response.data);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingSheet(null);
    setShowForm(true);
  };

  const handleEdit = async (sheet: any) => {
    // Cargar los datos actualizados de la ficha individual para obtener valores recalculados
    try {
      let response;
      if (activeTab === 'crv') {
        response = await investmentSheetCrvService.getById(sheet.id!);
      } else if (activeTab === 'cra') {
        response = await investmentSheetCraService.getById(sheet.id!);
      } else {
        response = await investmentSheetPhService.getById(sheet.id!);
      }
      setEditingSheet(response.data);
      setShowForm(true);
    } catch (error) {
      console.error('Error loading sheet:', error);
      // En caso de error, usar los datos de la lista
      setEditingSheet(sheet);
      setShowForm(true);
    }
  };

  const handleDeleteClick = (id: string, name: string) => {
    setSheetToDelete({ id, name });
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!sheetToDelete) return;

    try {
      if (activeTab === 'crv') {
        await investmentSheetCrvService.delete(sheetToDelete.id);
      } else if (activeTab === 'cra') {
        await investmentSheetCraService.delete(sheetToDelete.id);
      } else {
        await investmentSheetPhService.delete(sheetToDelete.id);
      }
      showToast('Ficha de inversión eliminada correctamente');
      loadData();
      setShowDeleteModal(false);
      setSheetToDelete(null);
    } catch (error) {
      console.error('Error deleting sheet:', error);
      showToast('Error al eliminar la ficha de inversión', 'error');
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setSheetToDelete(null);
  };

  const handleAssignTickets = (sheet: InvestmentSheetCra) => {
    setAssignTicketsSheet({
      id: sheet.id!,
      investorName: getInvestorName(sheet.investorId),
    });
    setShowAssignTicketsModal(true);
  };

  const handleAssignTicketsSuccess = () => {
    setShowAssignTicketsModal(false);
    setAssignTicketsSheet(null);
    loadData();
  };

  const handleAssignTicketsCancel = () => {
    setShowAssignTicketsModal(false);
    setAssignTicketsSheet(null);
  };

  const clearFilters = () => {
    setFilterInvestor('');
    setFilterProduct('');
    setFilterImporte('');
    setFilterEstado('');
    setCurrentPage(1);
  };

  const handleFilterChange = (setter: (v: string) => void) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setter(e.target.value);
    setCurrentPage(1);
  };

  const filterData = (data: any[]) => {
    return data.filter((sheet) => {
      const matchesInvestor = !filterInvestor ||
        getInvestorName(sheet.investorId).toLowerCase().includes(filterInvestor.toLowerCase());
      const matchesProduct = !filterProduct ||
        getProductName(sheet.productId).toLowerCase().includes(filterProduct.toLowerCase());
      const matchesImporte = !filterImporte ||
        sheet.importe?.toString().includes(filterImporte);
      const matchesEstado = !filterEstado ||
        sheet.estado?.toLowerCase().includes(filterEstado.toLowerCase());
      return matchesInvestor && matchesProduct && matchesImporte && matchesEstado;
    });
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingSheet(null);
    loadData();
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingSheet(null);
  };

  const formatCurrency = (value?: number) => {
    if (value === undefined || value === null) return '-';
    return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(value);
  };

  const formatDate = (date?: string) => {
    if (!date) return '-';
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const getInvestorName = (investorId: string) => {
    const investor = investors.find(i => i.id === investorId);
    return investor?.nombre || investorId;
  };

  const getProductName = (productId: string) => {
    const product = products.find(p => p.id === productId);
    return product?.nombreProducto || productId;
  };


  const renderPaginator = (total: number) => {
    if (total <= PAGE_SIZE) return null;
    const totalPages = Math.ceil(total / PAGE_SIZE);
    const from = (currentPage - 1) * PAGE_SIZE + 1;
    const to = Math.min(currentPage * PAGE_SIZE, total);

    // Show max 7 page buttons, with ellipsis
    const pages: (number | '...')[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }

    return (
      <div className="paginator">
        <div className="paginator-controls">
          <button
            className="paginator-btn"
            onClick={() => setCurrentPage(p => p - 1)}
            disabled={currentPage === 1}
          >
            ‹
          </button>
          {pages.map((p, i) =>
            p === '...' ? (
              <span key={`ellipsis-${i}`} className="paginator-ellipsis">…</span>
            ) : (
              <button
                key={p}
                className={`paginator-btn${currentPage === p ? ' active' : ''}`}
                onClick={() => setCurrentPage(p as number)}
              >
                {p}
              </button>
            )
          )}
          <button
            className="paginator-btn"
            onClick={() => setCurrentPage(p => p + 1)}
            disabled={currentPage === totalPages}
          >
            ›
          </button>
        </div>
        <span className="paginator-info">Mostrando {from}–{to} de {total}</span>
      </div>
    );
  };

  const renderTable = () => {
    let data: any[] = [];
    let columns: { label: string; align?: string }[] = [];

    if (activeTab === 'crv') {
      data = filterData(crvSheets);
      columns = [
        { label: 'ID' }, { label: 'Inversor' }, { label: 'Producto' }, { label: 'Fase' },
        { label: 'Importe', align: 'right' }, { label: 'Estado' }, { label: 'Fecha Contrato', align: 'right' },
      ];
    } else if (activeTab === 'cra') {
      data = filterData(craSheets);
      columns = [
        { label: 'ID' }, { label: 'Inversor' }, { label: 'Producto' }, { label: 'Fase' },
        { label: 'Importe', align: 'right' }, { label: 'Nº Tickets', align: 'right' }, { label: 'Estado' },
      ];
    } else {
      data = filterData(phSheets);
      columns = [
        { label: 'ID' }, { label: 'Inversor' }, { label: 'Producto' }, { label: 'Fase' },
        { label: 'Importe', align: 'right' }, { label: 'Estado' }, { label: 'Fecha Contrato', align: 'right' },
      ];
    }

    if (loading) {
      return <LoadingSpinner />;
    }

    if (data.length === 0) {
      return <EmptyState icon={BsGraphUp} title="No hay fichas de inversión registradas" subtitle="Añade la primera con el botón + Nueva ficha" />;
    }

    const total = data.length;
    const pageData = data.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

    return (
      <>
        <table className="investment-sheets-table">
          <thead>
            <tr>
              {columns.map((col) => (
                <th key={col.label} className={col.align === 'right' ? 'td-right' : undefined}>{col.label}</th>
              ))}
              <th className="col-actions">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {pageData.map((sheet) => (
              <tr key={sheet.id}>
                <td className="id-cell">{sheet.id}</td>
                <td>{getInvestorName(sheet.investorId)}</td>
                <td>{getProductName(sheet.productId)}</td>
                <td>{sheet.phaseId}</td>
                <td className="td-right">{formatCurrency(sheet.importe)}</td>
                {activeTab === 'cra' && (
                  <td className="td-right">{(sheet as InvestmentSheetCra).numTickets || '-'}</td>
                )}
                {(activeTab === 'crv' || activeTab === 'ph') && (
                  <></>
                )}
                <td>
                  <span className={`status-badge status-${sheet.estado?.toLowerCase().replace(/\./g, '').replace(/\s+/g, '-')}`}>
                    {sheet.estado || '-'}
                  </span>
                </td>
                {(activeTab === 'crv' || activeTab === 'ph') && (
                  <td className="td-right">{formatDate(sheet.fechaContrato)}</td>
                )}
                <td className="col-actions">
                  <div className="actions">
                    {activeTab === 'cra' && (
                      <ActionButton
                        icon={FaTicketAlt}
                        text="Asignar Tickets"
                        onClick={() => handleAssignTickets(sheet as InvestmentSheetCra)}
                        variant="detail"
                        disabled={!!((sheet as InvestmentSheetCra).numTickets && (sheet as InvestmentSheetCra).numTickets! > 0)}
                      />
                    )}
                    <ActionButton
                      icon={FaEdit}
                      text="Editar Inversión"
                      onClick={() => handleEdit(sheet)}
                      variant="edit"
                    />
                    <ActionButton
                      icon={FaTrash}
                      text="Eliminar Inversión"
                      onClick={() => handleDeleteClick(sheet.id!, sheet.id!)}
                      variant="delete"
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {renderPaginator(total)}
      </>
    );
  };

  return (
    <div className="investment-sheets-page">
      <AssignTicketsModal
        isOpen={showAssignTicketsModal}
        sheetId={assignTicketsSheet?.id || ''}
        investorName={assignTicketsSheet?.investorName || ''}
        onSuccess={handleAssignTicketsSuccess}
        onCancel={handleAssignTicketsCancel}
      />

      <ConfirmModal
        isOpen={showDeleteModal}
        title="Confirmar eliminación"
        message={`¿Estás seguro de que deseas eliminar la ficha ${sheetToDelete?.name}? Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        cancelText="Cancelar"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        type="danger"
      />

      <div className="tabs">
        <button
          className={`tab ${activeTab === 'crv' ? 'active' : ''}`}
          onClick={() => setActiveTab('crv')}
        >
          {SHEET_TYPE_LABELS.crv}
        </button>
        <button
          className={`tab ${activeTab === 'cra' ? 'active' : ''}`}
          onClick={() => setActiveTab('cra')}
        >
          {SHEET_TYPE_LABELS.cra}
        </button>
        <button
          className={`tab ${activeTab === 'ph' ? 'active' : ''}`}
          onClick={() => setActiveTab('ph')}
        >
          {SHEET_TYPE_LABELS.ph}
        </button>
        <div className="tab-actions">
          <ActionButton
            icon={FaPlus}
            text="Crear Inversión"
            onClick={handleCreate}
            variant="primary"
          />
        </div>
      </div>

      {!showForm && (
        <div className="filters-container">
          <div className="filters">
          <div className="filter-group">
            <input
              type="text"
              placeholder="Buscar por inversor..."
              value={filterInvestor}
              onChange={handleFilterChange(setFilterInvestor)}
              className="filter-input"
            />
          </div>
          <div className="filter-group">
            <input
              type="text"
              placeholder="Buscar por producto..."
              value={filterProduct}
              onChange={handleFilterChange(setFilterProduct)}
              className="filter-input"
            />
          </div>
          <div className="filter-group">
            <input
              type="text"
              placeholder="Buscar por importe..."
              value={filterImporte}
              onChange={handleFilterChange(setFilterImporte)}
              className="filter-input"
            />
          </div>
          <div className="filter-group">
            <input
              type="text"
              placeholder="Buscar por estado..."
              value={filterEstado}
              onChange={handleFilterChange(setFilterEstado)}
              className="filter-input"
            />
          </div>
          {(filterInvestor || filterProduct || filterImporte || filterEstado) && (
            <button onClick={clearFilters} className="btn-clear-filters">
              Limpiar filtros
            </button>
          )}
        </div>
        </div>
      )}

      <div className="investment-sheets-content">
        {!showForm ? (
          renderTable()
        ) : (
          <div className="form-container">
            {activeTab === 'crv' && (
              <InvestmentCrvForm
                sheet={editingSheet}
                onSuccess={handleFormSuccess}
                onCancel={handleFormCancel}
              />
            )}
            {activeTab === 'ph' && (
              <InvestmentPhForm
                sheet={editingSheet}
                onSuccess={handleFormSuccess}
                onCancel={handleFormCancel}
              />
            )}
            {activeTab === 'cra' && (
              <InvestmentCraForm
                sheet={editingSheet}
                onSuccess={handleFormSuccess}
                onCancel={handleFormCancel}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Investment;
